import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Section from "../../components/Section";
import SuccessNotification from "../../components/Notifications/SuccessNotification";
import SuccessProcess from '../../components/Notifications/SuccessProcess';
import ErrorNotification from "../../components/Notifications/ErrorNotification";
import LoginNotification from "../../components/Notifications/LoginNotification";
import Loader from "../../components/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faRotate, faCircleArrowLeft, faXmark } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ProductPriceForm = ({ handleLogout }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [modulePack, setModulePack] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [purchasePrice, setPurchasePrice] = useState(0);
    const [perUserPrice, setPerUserPrice] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [expirationDate, setExpirationDate] = useState(null);
    const [flagActive, setFlagActive] = useState(0);
    const [isSuccessVisible, setIsSuccessVisible] = useState(false);
    const [isErrorVisible, setIsErrorVisible] = useState(false);
    const [errorsMessage, setErrorMessage] = useState({});
    const [isLoginVisible, setIsLoginVisible] = useState(false);
    const [errors, setErrors] = useState({});
    const [notificationBlock, setNotificationBlock] = useState(false);
    const [messageBlock, setMessageBlock] = useState(false);

    const handleEffectiveDateChange = (date) => {
        if (date) {
            const formattedDate = format(date, "yyyy-MM-dd HH:mm:ss");
            setEffectiveDate(date ? formattedDate : null);
        }
    };

    const handleExpirationDateChange = (date) => {
        if (date) {
            const formattedDate = format(date, "yyyy-MM-dd HH:mm:ss");
            setExpirationDate(date ? formattedDate : null);
        }
    };

    const handleCloseBlock = () => {
        setNotificationBlock(false);
    };

    useEffect(() => {
        if (id) {
            const fetchProductPrice = async () => {
                try {
                    setLoading(true);
                    const response = await apiClient.get(`/product_price/${id}`);
                    setTemplateId(response.data.templateId);
                    setPurchasePrice(response.data.purchasePrice);
                    setPerUserPrice(response.data.perUserPrice);
                    setDiscount(response.data.discount);
                    setEffectiveDate(response.data.effectiveDate);
                    setExpirationDate(response.data.expirationDate);
                    setFlagActive(response.data.flagActive ? 1 : 0);
                } catch (error) {
                    console.error("Error al cargar el precio de producto", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchProductPrice();
        }
        const fetchModulePacks = async () => {
            try {
                setLoading(true);
                const modPacks = await apiClient.get(`/module_pack`);
                if (modPacks.status === 200) {
                    setModulePack(modPacks.data || []);
                }
            } catch (error) {
                console.error("Error al cargar los módulos (module pack)", error);
            } finally {
                setLoading(false);
            }
        };
        fetchModulePacks();
    }, [id]);

    const toggleSwitchFlag = () => {
        setFlagActive((prev) => (prev === 1 ? 0 : 1));
    };

    const validateFields = () => {
        let validationErrors = {};
        if (!templateId) validationErrors.templateId = "El Campo es Obligatorio";
        if (!purchasePrice && purchasePrice !== 0) validationErrors.purchasePrice = "El Campo es Obligatorio";
        if (!perUserPrice && perUserPrice !== 0) validationErrors.perUserPrice = "El Campo es Obligatorio";
        if (!discount && discount !== 0) validationErrors.discount = "El Campo es Obligatorio";
        if (!effectiveDate) {
            validationErrors.effectiveDate = "El Campo es Obligatorio";
        } else {
            const selectedDate = new Date(effectiveDate);
            if (isNaN(selectedDate.getTime())) {
                validationErrors.effectiveDate = "Fecha inválida";
            }
        }
        if (expirationDate) {
            const selectedDateEx = new Date(expirationDate);
            if (isNaN(selectedDateEx.getTime())) {
                validationErrors.effectiveDate = "Fecha inválida";
            }
        }
        return validationErrors;
    };

    const handleSave = async () => {
        const validationErrors = validateFields();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        try {
            let response;
            setLoading(true);
            if (id) {
                const payloadUpdate = {
                    purchasePrice,
                    perUserPrice,
                    discount,
                    effectiveDate: effectiveDate ? format(new Date(effectiveDate), "yyyy-MM-dd HH:mm:ss") : null,
                    expirationDate: expirationDate ? format(new Date(expirationDate), "yyyy-MM-dd HH:mm:ss") : null,
                    flagActive: flagActive === 1
                };
                response = await apiClient.patch(`/product_price/${id}`, payloadUpdate);
                if (response.status === 200) {
                    setIsSuccessVisible(true);
                    setIsErrorVisible(false);
                    setIsLoginVisible(false);
                    setErrors({});
                    navigate("/ProductPriceList");
                }
            } else {
                const payload = {
                    templateId,
                    purchasePrice,
                    perUserPrice,
                    discount,
                    effectiveDate: effectiveDate ? format(new Date(effectiveDate), "yyyy-MM-dd HH:mm:ss") : null,
                    flagActive: flagActive === 1
                };
                response = await apiClient.post("/product_price", payload);
                if (response.status === 201) {
                    setIsSuccessVisible(true);
                    setIsErrorVisible(false);
                    setErrors({});
                    navigate("/ProductPriceList");
                }
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setIsLoginVisible(true);
            } else if (error.response?.data?.message.startsWith("PRODUCT_PRICE_ALREADY_EXIST")) {
                setMessageBlock("El Precio de Producto ya existe.");
                setNotificationBlock(true);
            } else if (error.response?.data?.message.startsWith("PRODUCT_ALREADY_PRICE_RELATED")) {
                setMessageBlock("El módulo ya tiene un precio de producto designado.");
                setNotificationBlock(true);
            } else {
                setErrorMessage("¡Oops! Ocurrio un error al crear el Precio de Producto");
                setIsLoginVisible(false);
                setIsErrorVisible(true);
                setIsSuccessVisible(false);
                setTimeout(() => {
                    setIsErrorVisible(false);
                }, 4000);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="home-container">
            <Header onLogout={handleLogout} title="Precios de Producto" />
            <Section>
                <div className="button-return-container">
                    <FontAwesomeIcon
                        className="basic-shortcut-icon"
                        style={{ cursor: "pointer" }}
                        icon={faCircleArrowLeft}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate("/ProductPriceList");
                        }}
                    />
                </div>
            </Section>
            <div className="moduleNew-form-container">
                <h3 className="basic-info-form-title">Información del Precio de Producto</h3>
                <div className="basic-info-form-grid">
                    <div className="basic-info-form-group">
                        <label style={{ color: errors.templateId ? "red" : "revert-layer" }}>Módulo</label>
                        <select
                            value={templateId}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                setTemplateId(selectedId);
                            }
                            }
                            disabled={id !== undefined}
                        >
                            <option value="">Seleccione una opción</option>
                            {modulePack.map((mod) => (
                                <option key={mod.id} value={mod.id}>
                                    {mod.description}
                                </option>
                            ))}
                        </select>
                        {errors.templateId && <p className="error-message">{errors.templateId}</p>}
                    </div>
                    <div className="basic-info-form-group">
                        <label style={{ color: errors.purchasePrice ? "red" : "revert-layer" }}>
                            Precio de Compra
                        </label>
                        <input
                            type="text"
                            placeholder="Precio de Compra"
                            value={purchasePrice}
                            //onChange={(e) => setPurchasePrice(e.target.value)}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^(?:\d+\.?\d*|\d*)$/.test(value)) {
                                    setPurchasePrice(value);
                                }
                            }}
                        />
                        {errors.purchasePrice && <p className="error-message">{errors.purchasePrice}</p>}
                    </div>
                    <div className="basic-info-form-group">
                        <label style={{ color: errors.perUserPrice ? "red" : "revert-layer" }}>
                            Precio por Usuario
                        </label>
                        <input
                            type="text"
                            placeholder="Precio por Usuario"
                            value={perUserPrice}
                            //onChange={(e) => setPerUserPrice(e.target.value)}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^(?:\d+\.?\d*|\d*)$/.test(value)) {
                                    setPerUserPrice(value);
                                }
                            }}
                        />
                        {errors.perUserPrice && <p className="error-message">{errors.perUserPrice}</p>}
                    </div>
                    <div className="basic-info-form-group">
                        <label style={{ color: errors.discount ? "red" : "revert-layer" }}>
                            Descuento
                        </label>
                        <input
                            type="text"
                            placeholder="Descuento"
                            value={discount}
                            //onChange={(e) => setDiscount(e.target.value)}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (/^(?:\d+\.?\d*|\d*)$/.test(value)) {
                                    setDiscount(value);
                                }
                            }}
                        />
                        {errors.discount && <p className="error-message">{errors.discount}</p>}
                    </div>
                    <div className="basic-info-form-group">
                        <label style={{ color: errors.effectiveDate ? "red" : "revert-layer" }}>
                            Fecha de Vigencia{" "}
                        </label>
                        <div className="basic-info-date-picker">
                            <DatePicker
                                selected={effectiveDate ? new Date(effectiveDate) : null}
                                onChange={handleEffectiveDateChange}
                                showTimeSelect
                                locale={es}
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                placeholderText="Selecciona la Fecha"
                                className="custom-date-picker"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                            {errors.effectiveDate && <p className="error-message">{errors.effectiveDate}</p>}
                        </div>
                    </div>

                    {id && (
                        <div className="basic-info-form-group">
                            <label style={{ color: errors.expirationDate ? "red" : "revert-layer" }}>
                                Fecha de Expiración{" "}
                            </label>
                            <div className="basic-info-date-picker">
                                <DatePicker
                                    selected={expirationDate ? new Date(expirationDate) : null}
                                    onChange={handleExpirationDateChange}
                                    showTimeSelect
                                    locale={es}
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    placeholderText="Selecciona la Fecha"
                                    className="custom-date-picker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                                {errors.expirationDate && <p className="error-message">{errors.expirationDate}</p>}
                            </div>
                        </div>
                    )}
                    <div className="basic-info-form-group">
                        <div className="basic-info-form-switch">
                            <label>Estado</label>
                            <div className="slider-container-contract" onClick={toggleSwitchFlag}>
                                <div className={`slider-option-contract ${flagActive === 1
                                    ? "active-contract"
                                    : "inactive-contract"}`}>
                                    Activo
                                </div>
                                <div className={`slider-option-contract ${flagActive === 0
                                    ? "active-contract"
                                    : "inactive-contract"}`}>
                                    Inactivo
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="basic-form-footer">
                    <button
                        className="basic-custom-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate("/ProductPriceList");
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} className="basic-shortcut-icon" />
                        Cancelar
                    </button>
                    <button
                        className="basic-custom-button"
                        onClick={handleSave}
                    >
                        <FontAwesomeIcon
                            icon={id ? faRotate : faSave}
                            className="basic-shortcut-icon"
                        />
                        {id ? "Actualizar" : "Guardar"}
                    </button>
                </div>
            </div>
            <SuccessNotification
                message={
                    id
                        ? "El Precio de Producto se ha Actualizado Correctamente."
                        : "El Precio de Producto se ha Creado Correctamente."
                }
                isVisible={isSuccessVisible}
                onClose={() => setIsSuccessVisible(false)}
            />
            <ErrorNotification
                message={errorsMessage}
                isVisible={isErrorVisible}
                onClose={() => setIsErrorVisible(false)}
            />
            <LoginNotification
                message="Vuelve a iniciar sesión"
                isVisible={isLoginVisible}
                onClose={() => setIsLoginVisible(false)}
                onLogout={handleLogout}
            />
            {notificationBlock && (
                <SuccessProcess
                    enca={true}
                    message={`No se puede Crear el Precio de Producto debido a : ${messageBlock}`}
                    isVisible={notificationBlock}
                    onClose={handleCloseBlock}
                    redirectOnOk={true}
                    pageNavigate={"/ProductPriceList"}
                    whatsApp={false} 
                />
            )}
            {loading && <Loader />}
        </div>
    );
};

export default ProductPriceForm;
